import { useEffect, useMemo, useState } from 'react';
import { Tabs, Table, Button, Input, Select, Avatar, Tag, Space, Modal, Form, App, Dropdown, Switch, Divider, Upload } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Plus, RefreshCw, Search, Edit, Lock, Unlock, Key, Shield, Monitor, MoreVertical, Upload as UploadIcon, Download, FileSpreadsheet, AlertCircle, CheckCircle2 } from 'lucide-react';
import { PageHeader, Card } from '@/components/common/PageHeader';
import type { User } from '@/types';
import { userService, type UserSession } from '@/services/userService';
import { createCategoryService } from '@/services/categoryService';
import { rolesService } from '@/services/rolesService';
import { useQuery } from '@tanstack/react-query';
import { RolesMatrixTab } from './RolesMatrixTab';
import { OrgTreeTab } from './OrgTreeTab';
import { GENDER_OPTIONS, getGenderLabel, getGenderColor } from '@/utils/genderHelper';  // V1.0.48

const deptService = createCategoryService<any>('departments');

const ROLE_COLORS: Record<string, string> = {
  SYS_ADMIN: 'red', BOD: 'purple', DEPT_HEAD: 'cyan', STAFF: 'default', WATCHER: 'default',
};

const STATUS_COLORS: Record<string, string> = {
  ACTIVE: 'success', LOCKED: 'warning', INACTIVE: 'default',
};

const STATUS_LABELS: Record<string, string> = {
  ACTIVE: '● Active', LOCKED: '🔒 Khóa', INACTIVE: '○ Inactive',
};

// ============================================================
// Main Page
// ============================================================
export function UsersPage() {
  const [tab, setTab] = useState('users');

  return (
    <>
      <PageHeader
        title="Quản lý người dùng"
        subtitle="Người dùng · Vai trò · Cơ cấu tổ chức · Ma trận quyền"
      />
      <Card padding={0}>
        <Tabs
          activeKey={tab}
          onChange={setTab}
          style={{ padding: '0 20px', borderBottom: '1px solid #eceef1' }}
          items={[
            { key: 'users', label: 'Người dùng' },
            { key: 'org', label: 'Cơ cấu tổ chức' },
            { key: 'matrix', label: 'Vai trò & Ma trận quyền' },
          ]}
        />
        {tab === 'users' && <UsersTab />}
        {tab === 'org' && <OrgTreeTab />}
        {tab === 'matrix' && <RolesMatrixTab />}
      </Card>
    </>
  );
}

// ============================================================
// Users Tab
// ============================================================
function UsersTab() {
  const { message, modal } = App.useApp();
  const [data, setData] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [deptFilter, setDeptFilter] = useState<string | undefined>();
  const [roleFilter, setRoleFilter] = useState<string | undefined>();
  const [statusFilter, setStatusFilter] = useState<string | undefined>();

  const [departments, setDepartments] = useState<any[]>([]);

  const [userModal, setUserModal] = useState<{ mode: 'create' | 'edit'; user?: User } | null>(null);
  const [roleModal, setRoleModal] = useState<User | null>(null);
  const [sessionModal, setSessionModal] = useState<User | null>(null);
  const [importModalOpen, setImportModalOpen] = useState(false); // V1.0.33

  // Fetch roles từ DB (replace hardcode)
  const { data: rolesFromDb = [] } = useQuery({
    queryKey: ['roles-simple'],
    queryFn: () => rolesService.listSimple(),
    staleTime: 5 * 60 * 1000, // cache 5 phút
  });
  const roleOptions = useMemo(() =>
    rolesFromDb.map(r => ({ value: r.code, label: `${r.code} — ${r.name}` })),
    [rolesFromDb]
  );

  const load = async () => {
    setLoading(true);
    try {
      const [users, depts] = await Promise.all([
        userService.list({ pageSize: 200, search: search || undefined }),
        deptService.list().catch(() => []),
      ]);
      setData(users.items);
      setDepartments(depts);
    } catch (e: any) {
      message.error('Không tải được danh sách: ' + (e.response?.data?.message ?? e.message));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => data.filter(u => {
    if (deptFilter && u.department?.id !== deptFilter) return false;
    if (roleFilter && !u.roles?.includes(roleFilter as any)) return false;
    if (statusFilter && u.status !== statusFilter) return false;
    return true;
  }), [data, deptFilter, roleFilter, statusFilter]);

  const handleToggleLock = async (u: User) => {
    const next = u.status === 'LOCKED' ? 'ACTIVE' : 'LOCKED';
    modal.confirm({
      title: next === 'LOCKED' ? `Khóa tài khoản ${u.fullName}?` : `Mở khóa ${u.fullName}?`,
      content: next === 'LOCKED'
        ? 'Tài khoản sẽ không thể đăng nhập. Tất cả phiên hiện tại sẽ bị hủy.'
        : 'Tài khoản sẽ đăng nhập bình thường trở lại.',
      okText: next === 'LOCKED' ? 'Khóa' : 'Mở khóa',
      okButtonProps: { danger: next === 'LOCKED' },
      onOk: async () => {
        try {
          await userService.setStatus(u.id, next as any);
          message.success(next === 'LOCKED' ? 'Đã khóa' : 'Đã mở khóa');
          load();
        } catch (e: any) {
          message.error(e.response?.data?.message ?? e.message);
        }
      },
    });
  };

  const handleResetPassword = (u: User) => {
    modal.confirm({
      title: `Reset mật khẩu cho ${u.fullName}?`,
      content: 'Mật khẩu mới sẽ được sinh ngẫu nhiên. User sẽ phải đổi khi login lần kế.',
      okText: 'Reset',
      onOk: async () => {
        try {
          const result = await userService.resetPassword(u.id);
          modal.success({
            title: 'Mật khẩu mới',
            content: (
              <div>
                <p>Password tạm thời — copy và gửi cho <b>{u.fullName}</b>:</p>
                <Input.Password defaultValue={result.password} readOnly visibilityToggle={{ visible: true }} />
                <p style={{ color: '#d97706', fontSize: 12, marginTop: 8 }}>⚠ Password chỉ hiển thị 1 lần.</p>
              </div>
            ),
            width: 500,
          });
        } catch (e: any) {
          message.error(e.response?.data?.message ?? e.message);
        }
      },
    });
  };

  const columns: ColumnsType<User> = [
    {
      title: 'Người dùng', dataIndex: 'fullName',
      render: (_: string, u: User) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Avatar style={{ background: '#2a3547', color: '#6699CC' }}>{u.fullName.slice(-2)}</Avatar>
          <div>
            <div style={{ fontWeight: 500 }}>{u.fullName}</div>
            <div style={{ fontSize: 11, color: '#adb6c0' }}>{u.email}</div>
          </div>
        </div>
      ),
    },
    { title: 'Username', dataIndex: 'username', width: 120, render: (v: string) => <span className="mono" style={{ fontSize: 11 }}>{v}</span> },
    { title: 'Khoa/Phòng', width: 180, render: (_: any, u: User) => u.department?.name ?? '-' },
    {
      title: 'Giới tính', dataIndex: 'gender', width: 110,
      render: (g: string) => <Tag color={getGenderColor(g)} style={{ fontSize: 11 }}>{getGenderLabel(g)}</Tag>,
    },
    {
      title: 'Vai trò', dataIndex: 'roles', width: 180,
      render: (roles: string[]) => (
        <>{(roles || []).map(r => <Tag color={ROLE_COLORS[r]} key={r} style={{ fontSize: 10 }}>{r}</Tag>)}</>
      ),
    },
    {
      title: 'Trạng thái', dataIndex: 'status', width: 110,
      render: (s: string) => <Tag color={STATUS_COLORS[s]}>{STATUS_LABELS[s]}</Tag>,
    },
    {
      title: 'Lần cuối', dataIndex: 'lastLoginAt', width: 140,
      render: (t: string) => t ? <span className="mono" style={{ fontSize: 10, color: '#7d8a98' }}>{new Date(t).toLocaleString('vi-VN')}</span> : '-',
    },
    {
      title: '', width: 60, fixed: 'right' as const,
      render: (_: any, u: User) => (
        <Dropdown
          menu={{
            items: [
              { key: 'edit', label: 'Sửa thông tin', icon: <Edit size={12} />, onClick: () => setUserModal({ mode: 'edit', user: u }) },
              { key: 'roles', label: 'Gán vai trò', icon: <Shield size={12} />, onClick: () => setRoleModal(u) },
              { key: 'reset', label: 'Reset mật khẩu', icon: <Key size={12} />, onClick: () => handleResetPassword(u) },
              { key: 'sessions', label: 'Xem phiên đăng nhập', icon: <Monitor size={12} />, onClick: () => setSessionModal(u) },
              { type: 'divider' as const },
              {
                key: 'lock',
                label: u.status === 'LOCKED' ? 'Mở khóa' : 'Khóa tài khoản',
                icon: u.status === 'LOCKED' ? <Unlock size={12} /> : <Lock size={12} />,
                danger: u.status !== 'LOCKED',
                onClick: () => handleToggleLock(u),
              },
            ],
          }}
          trigger={['click']}
        >
          <Button type="text" icon={<MoreVertical size={16} />} />
        </Dropdown>
      ),
    },
  ];

  return (
    <>
      <div style={{ padding: 16, borderBottom: '1px solid #eceef1', background: '#f6f7f8', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <Space wrap>
          <Input
            placeholder="Tìm theo tên, username, email..."
            prefix={<Search size={14} />}
            style={{ width: 260 }}
            value={search}
            onChange={e => setSearch(e.target.value)}
            onPressEnter={load}
            allowClear
            onClear={() => { setSearch(''); setTimeout(load, 0); }}
          />
          <Select placeholder="Khoa/Phòng" style={{ width: 180 }} allowClear value={deptFilter} onChange={setDeptFilter}
            options={departments.map(d => ({ value: d.id, label: d.name }))} />
          <Select placeholder="Vai trò" style={{ width: 140 }} allowClear value={roleFilter} onChange={setRoleFilter}
            options={roleOptions} />
          <Select placeholder="Trạng thái" style={{ width: 140 }} allowClear value={statusFilter} onChange={setStatusFilter}
            options={[
              { value: 'ACTIVE', label: 'Active' },
              { value: 'LOCKED', label: 'Locked' },
              { value: 'INACTIVE', label: 'Inactive' },
            ]} />
        </Space>
        <Space>
          <Button icon={<RefreshCw size={14} />} onClick={load}>Tải lại</Button>
          <Button icon={<UploadIcon size={14} />} onClick={() => setImportModalOpen(true)}>
            Import Excel
          </Button>
          <Button type="primary" icon={<Plus size={14} />} onClick={() => setUserModal({ mode: 'create' })}>Thêm người dùng</Button>
        </Space>
      </div>

      <Table columns={columns} dataSource={filtered} rowKey="id" size="middle"
        loading={loading} scroll={{ x: 900 }} pagination={{ pageSize: 20 }} />

      {userModal && (
        <UserFormModal
          mode={userModal.mode}
          user={userModal.user}
          departments={departments}
          onClose={() => setUserModal(null)}
          onSuccess={() => { setUserModal(null); load(); }}
        />
      )}
      {importModalOpen && (
        <ImportUsersModal
          onClose={() => setImportModalOpen(false)}
          onSuccess={() => { setImportModalOpen(false); load(); }}
        />
      )}
      {roleModal && (
        <AssignRolesModal user={roleModal} onClose={() => setRoleModal(null)}
          onSuccess={() => { setRoleModal(null); load(); }} />
      )}
      {sessionModal && (
        <UserSessionsModal user={sessionModal} onClose={() => setSessionModal(null)} />
      )}
    </>
  );
}

// ============================================================
// User Form Modal
// ============================================================
function UserFormModal({ mode, user, departments, onClose, onSuccess }: {
  mode: 'create' | 'edit'; user?: User; departments: any[]; onClose: () => void; onSuccess: () => void;
}) {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const { data: rolesFromDb = [] } = useQuery({
    queryKey: ['roles-simple'],
    queryFn: () => rolesService.listSimple(),
    staleTime: 5 * 60 * 1000,
  });
  const roleOptions = rolesFromDb.map(r => ({ value: r.code, label: `${r.code} — ${r.name}` }));

  useEffect(() => {
    if (mode === 'edit' && user) {
      form.setFieldsValue({
        username: user.username, fullName: user.fullName, email: user.email,
        phone: user.phone, departmentId: user.department?.id,
        position: user.position, roles: user.roles || [],
        otpRequired: user.otpRequired ?? true,
        gender: user.gender ?? 'UNKNOWN',  // V1.0.48
      });
      // V1.0.63: Load danh sách khoa user đang quản lý
      userService.getManagedDepartments(user.id)
        .then(depts => {
          form.setFieldsValue({ managedDeptIds: depts.map(d => d.id) });
        })
        .catch(() => {
          form.setFieldsValue({ managedDeptIds: [] });
        });
    } else {
      form.resetFields();
      form.setFieldsValue({ otpRequired: true, gender: 'UNKNOWN', managedDeptIds: [] });
    }
  }, [mode, user, form]);

  const submit = async () => {
    try {
      const values = await form.validateFields();
      console.log('[UserForm] Form values:', values);
      setSaving(true);
      // V1.0.63: Tách managedDeptIds khỏi values để gọi riêng
      const managedDeptIds: string[] = values.managedDeptIds ?? [];
      if (mode === 'create') {
        const { managedDeptIds: _, ...createPayload } = values;
        const created = await userService.create(createPayload);
        // Nếu user mới có chọn khoa quản lý → call API set
        if (managedDeptIds.length > 0) {
          await userService.updateManagedDepartments(created.id, managedDeptIds);
        }
        message.success('Đã thêm. Mật khẩu mặc định: Cntt@2026');
      } else if (user) {
        const { username, managedDeptIds: _, ...rest } = values;
        // Đảm bảo payload có đủ field và đúng type
        const payload = {
          fullName: rest.fullName,
          email: rest.email,
          phone: rest.phone || undefined,
          departmentId: rest.departmentId,
          position: rest.position || undefined,
          roles: rest.roles,
          otpRequired: typeof rest.otpRequired === 'boolean' ? rest.otpRequired : undefined,
          gender: rest.gender || undefined,  // V1.0.48
        };
        console.log('[UserForm] Update payload:', payload, '→ user.id:', user.id);
        const updated = await userService.update(user.id, payload);
        // V1.0.63: Sync danh sách khoa quản lý
        await userService.updateManagedDepartments(user.id, managedDeptIds);
        console.log('[UserForm] Update response:', updated);
        message.success(`Đã cập nhật: ${updated.fullName}`);
      }
      onSuccess();
    } catch (e: any) {
      console.error('[UserForm] Update failed:', e);
      console.error('[UserForm] Response data:', e.response?.data);
      const errMsg = e.response?.data?.message
        ?? e.response?.data?.errors
        ? JSON.stringify(e.response?.data?.errors)
        : e.response?.data?.title
        ?? e.message
        ?? 'Có lỗi xảy ra';
      message.error(typeof errMsg === 'string' ? errMsg : JSON.stringify(errMsg));
    } finally { setSaving(false); }
  };

  return (
    <Modal title={mode === 'create' ? 'Thêm người dùng' : `Sửa: ${user?.fullName}`}
      open onCancel={onClose} onOk={submit} confirmLoading={saving}
      okText="Lưu" cancelText="Hủy" width={560}>
      <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
        <Form.Item name="username" label="Tên đăng nhập" rules={[{ required: true }]}>
          <Input disabled={mode === 'edit'} placeholder="vd: anhpn" />
        </Form.Item>
        <Form.Item name="fullName" label="Họ tên" rules={[{ required: true }]}>
          <Input placeholder="Phạm Ngọc Ánh" />
        </Form.Item>
        <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email' }]}>
          <Input placeholder="user@hungviet.vn" />
        </Form.Item>
        <Form.Item name="phone" label="SĐT">
          <Input placeholder="0912xxxxxx" />
        </Form.Item>
        <Form.Item name="departmentId" label="Khoa/Phòng" rules={[{ required: true }]}
          tooltip="Khoa chính của user (nơi user làm việc hằng ngày)">
          <Select showSearch optionFilterProp="label"
            options={departments.map(d => ({ value: d.id, label: `${d.code} · ${d.name}` }))}
            placeholder="Chọn khoa/phòng" />
        </Form.Item>

        {/* V1.0.63: 1 user quản lý nhiều khoa */}
        <Form.Item name="managedDeptIds" label="Khoa quản lý"
          tooltip="Các khoa user là trưởng đơn vị (tương đương DEPT_HEAD). Có thể chọn nhiều khoa. Để trống = không quản lý khoa nào.">
          <Select mode="multiple" showSearch optionFilterProp="label" allowClear
            options={departments.map(d => ({ value: d.id, label: `${d.code} · ${d.name}` }))}
            placeholder="Chọn các khoa user quản lý..."
            maxTagCount="responsive"
          />
        </Form.Item>

        <Form.Item name="position" label="Chức vụ">
          <Input placeholder="Chuyên viên / Trưởng phòng..." />
        </Form.Item>
        <Form.Item name="gender" label="Giới tính" tooltip="Lưu thông tin giới tính cho mục đích thống kê. Hệ thống xưng hô chung là 'bạn'.">
          <Select
            options={GENDER_OPTIONS}
            placeholder="Chọn giới tính"
          />
        </Form.Item>
        <Form.Item name="roles" label="Vai trò">
          <Select mode="multiple" options={roleOptions}
            placeholder="Chọn vai trò" />
        </Form.Item>

        <Divider style={{ margin: '12px 0' }} />

        <Form.Item
          name="otpRequired"
          label="Yêu cầu OTP khi ký điện tử"
          valuePropName="checked"
          extra={
            <div style={{ fontSize: 11, color: '#8a95a5', marginTop: 4 }}>
              <b>Bật:</b> Người dùng phải nhập mã OTP 6 số gửi qua email mỗi lần ký (an toàn hơn).<br />
              <b>Tắt:</b> Bỏ qua OTP - chỉ áp dụng cho user có USB Token/chữ ký số PKI cứng.
            </div>
          }
        >
          <Switch checkedChildren="Bật OTP" unCheckedChildren="Bỏ qua OTP" />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ============================================================
// Assign Roles Modal
// ============================================================
function AssignRolesModal({ user, onClose, onSuccess }: { user: User; onClose: () => void; onSuccess: () => void }) {
  const { message } = App.useApp();
  const [roles, setRoles] = useState<string[]>(user.roles || []);
  const [saving, setSaving] = useState(false);

  const { data: rolesFromDb = [] } = useQuery({
    queryKey: ['roles-simple'],
    queryFn: () => rolesService.listSimple(),
    staleTime: 5 * 60 * 1000,
  });
  const roleOptions = rolesFromDb.map(r => ({ value: r.code, label: `${r.code} — ${r.name}` }));

  const submit = async () => {
    setSaving(true);
    try {
      await userService.assignRoles(user.id, roles);
      message.success('Đã cập nhật vai trò');
      onSuccess();
    } catch (e: any) {
      message.error(e.response?.data?.message ?? e.message);
    } finally { setSaving(false); }
  };

  return (
    <Modal title={`Gán vai trò: ${user.fullName}`} open onCancel={onClose} onOk={submit}
      confirmLoading={saving} okText="Lưu" cancelText="Hủy">
      <p style={{ color: '#7d8a98', fontSize: 12 }}>Một user có thể có nhiều vai trò.</p>
      <Select mode="multiple" value={roles} onChange={setRoles}
        style={{ width: '100%', marginTop: 12 }}
        options={roleOptions}
        placeholder="Chọn vai trò" />
    </Modal>
  );
}

// ============================================================
// Sessions Modal
// ============================================================
function UserSessionsModal({ user, onClose }: { user: User; onClose: () => void }) {
  const { message, modal } = App.useApp();
  const [sessions, setSessions] = useState<UserSession[]>([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await userService.getSessions(user.id);
      setSessions(data);
    } catch (e: any) {
      message.error(e.response?.data?.message ?? e.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user.id]);

  const revoke = (s: UserSession) => {
    modal.confirm({
      title: 'Hủy phiên này?',
      content: `IP: ${s.ip ?? '?'} · tạo ${new Date(s.createdAt).toLocaleString('vi-VN')}`,
      onOk: async () => {
        try {
          await userService.revokeSession(s.id);
          message.success('Đã hủy phiên');
          load();
        } catch (e: any) {
          message.error(e.response?.data?.message ?? e.message);
        }
      },
    });
  };

  const columns: ColumnsType<UserSession> = [
    { title: 'IP', dataIndex: 'ip', width: 140, render: (v: string) => <span className="mono" style={{ fontSize: 11 }}>{v ?? '-'}</span> },
    { title: 'User-Agent', dataIndex: 'userAgent', ellipsis: true, render: (v: string) => <span style={{ fontSize: 11 }}>{v ?? '-'}</span> },
    { title: 'Tạo', dataIndex: 'createdAt', width: 140, render: (t: string) => <span style={{ fontSize: 11 }}>{new Date(t).toLocaleString('vi-VN')}</span> },
    { title: 'Hết hạn', dataIndex: 'expiresAt', width: 140, render: (t: string) => <span style={{ fontSize: 11 }}>{new Date(t).toLocaleString('vi-VN')}</span> },
    {
      title: 'Trạng thái', width: 110, dataIndex: 'isActive',
      render: (active: boolean, s: UserSession) => active
        ? <Tag color="success">Đang hoạt động</Tag>
        : s.revokedAt ? <Tag color="error">Đã hủy</Tag> : <Tag>Hết hạn</Tag>,
    },
    {
      title: '', width: 90,
      render: (_: any, s: UserSession) => s.isActive
        ? <Button size="small" danger onClick={() => revoke(s)}>Hủy</Button>
        : null,
    },
  ];

  return (
    <Modal title={`Phiên đăng nhập: ${user.fullName}`} open onCancel={onClose} footer={null} width={880}>
      <Table rowKey="id" columns={columns} dataSource={sessions} loading={loading}
        size="small" pagination={false} scroll={{ y: 400 }} />
    </Modal>
  );
}

// ============================================================
// V1.0.33: Import nhân viên từ Excel
// ============================================================
function ImportUsersModal({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }) {
  const { message } = App.useApp();
  const [file, setFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<any>(null);

  const handleUpload = async () => {
    if (!file) {
      message.warning('Vui lòng chọn file');
      return;
    }
    setImporting(true);
    try {
      const r = await userService.importUsers(file);
      setResult(r);
      if (r.successCount > 0) {
        message.success(`Đã import ${r.successCount}/${r.totalRows} nhân viên`);
      }
    } catch (e: any) {
      message.error(e.response?.data?.message ?? e.message ?? 'Lỗi import');
    } finally {
      setImporting(false);
    }
  };

  const handleDownloadTemplate = () => {
    try {
      userService.downloadImportTemplate();
      message.success('Đang tải file mẫu...');
    } catch (e: any) {
      message.error('Không tải được file mẫu: ' + e.message);
    }
  };

  // Nếu đã có result → hiển thị summary
  if (result) {
    return (
      <Modal
        title="Kết quả import nhân viên"
        open
        onCancel={() => { setResult(null); onClose(); }}
        width={720}
        footer={[
          result.successCount > 0 && (
            <Button key="continue" type="primary" onClick={() => { setResult(null); onSuccess(); }}>
              Hoàn tất ({result.successCount} nhân viên đã thêm)
            </Button>
          ),
          <Button key="close" onClick={() => { setResult(null); onClose(); }}>
            Đóng
          </Button>,
        ]}
      >
        {/* Stats */}
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12,
          marginBottom: 16,
        }}>
          <StatCard color="#1976D2" label="Tổng dòng" value={result.totalRows} />
          <StatCard color="#16a34a" label="Thành công" value={result.successCount} icon={<CheckCircle2 size={14} />} />
          <StatCard color="#cf1322" label="Lỗi" value={result.failedCount} icon={<AlertCircle size={14} />} />
          <StatCard color="#fa8c16" label="Bỏ qua" value={result.skippedCount} />
        </div>

        {/* Errors list */}
        {result.errors.length > 0 && (
          <div>
            <h4 style={{ margin: '12px 0 8px', color: '#cf1322' }}>
              ⚠️ Chi tiết lỗi ({result.errors.length})
            </h4>
            <Table
              size="small"
              dataSource={result.errors as any[]}
              rowKey={(r: any) => `${r.rowNumber}-${r.field}`}
              pagination={{ pageSize: 10 }}
              columns={[
                { title: 'Dòng', dataIndex: 'rowNumber', width: 60, align: 'center' },
                { title: 'Username', dataIndex: 'username', width: 140,
                  render: (v) => v || <span style={{ color: '#adb6c0' }}>—</span> },
                { title: 'Trường lỗi', dataIndex: 'field', width: 130,
                  render: (v) => <Tag color="orange">{v}</Tag> },
                { title: 'Lỗi', dataIndex: 'message' },
              ]}
            />
          </div>
        )}

        {/* Success list */}
        {result.createdUsernames.length > 0 && (
          <div>
            <h4 style={{ margin: '12px 0 8px', color: '#16a34a' }}>
              ✅ Đã tạo ({result.createdUsernames.length} nhân viên)
            </h4>
            <div style={{
              maxHeight: 100, overflowY: 'auto', padding: 8,
              background: '#f0f9ff', borderRadius: 6, fontSize: 12,
            }}>
              {result.createdUsernames.map((u: string) => (
                <Tag key={u} color="green" style={{ marginBottom: 4 }}>{u}</Tag>
              ))}
            </div>
            <div style={{ fontSize: 11, color: '#7d8a98', marginTop: 8 }}>
              Mật khẩu mặc định: <code>Cntt@2026</code> (hoặc password đã đặt trong Excel).
              Người dùng sẽ phải đổi mật khẩu sau lần đăng nhập đầu tiên.
            </div>
          </div>
        )}
      </Modal>
    );
  }

  // Form upload ban đầu
  return (
    <Modal
      title="Import nhân viên từ Excel"
      open
      onCancel={onClose}
      onOk={handleUpload}
      confirmLoading={importing}
      okText="Bắt đầu import"
      cancelText="Hủy"
      okButtonProps={{ disabled: !file }}
      width={560}
    >
      <div style={{ marginBottom: 16, padding: 12, background: '#f0f9ff', borderRadius: 6, fontSize: 12, color: '#0c4a6e' }}>
        <div style={{ fontWeight: 600, marginBottom: 6 }}>📋 Cách dùng:</div>
        <ol style={{ paddingLeft: 20, margin: 0 }}>
          <li>Tải file mẫu Excel bên dưới</li>
          <li>Điền danh sách nhân viên (theo cấu trúc cột có sẵn)</li>
          <li>Lưu file rồi upload lại tại đây</li>
          <li>Hệ thống sẽ tạo lần lượt, báo lỗi dòng nào không hợp lệ</li>
        </ol>
      </div>

      <div style={{ marginBottom: 12 }}>
        <Button icon={<Download size={14} />} onClick={handleDownloadTemplate} block>
          📄 Tải file mẫu Excel
        </Button>
      </div>

      <Upload.Dragger
        accept=".xlsx,.csv"
        beforeUpload={(f) => { setFile(f); return false; }}
        onRemove={() => setFile(null)}
        maxCount={1}
        fileList={file ? [{ uid: '-1', name: file.name, status: 'done' } as any] : []}
        style={{ padding: 20 }}
      >
        <p style={{ fontSize: 32, color: '#1976D2' }}>
          <FileSpreadsheet size={42} style={{ display: 'inline-block' }} />
        </p>
        <p style={{ fontSize: 14, fontWeight: 500 }}>
          {file ? `Đã chọn: ${file.name}` : 'Kéo thả file vào đây hoặc click để chọn'}
        </p>
        <p style={{ fontSize: 11, color: '#7d8a98' }}>
          Chấp nhận .xlsx hoặc .csv (tối đa 5MB)
        </p>
      </Upload.Dragger>

      <div style={{ marginTop: 12, fontSize: 11, color: '#5b6875' }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>⚠️ Lưu ý:</div>
        <ul style={{ paddingLeft: 20, margin: 0 }}>
          <li>Cột bắt buộc: <code>username, fullName, email, departmentCode</code></li>
          <li>Cột tùy chọn: <code>phone, position, roleCode, password</code></li>
          <li>roleCode hợp lệ: USER (mặc định), MANAGER, DEPT_HEAD, ADMIN</li>
          <li>Username/email trùng người đã có → bỏ qua</li>
          <li>departmentCode lấy ở menu Danh mục → Khoa/Phòng</li>
        </ul>
      </div>
    </Modal>
  );
}

function StatCard({ color, label, value, icon }: {
  color: string; label: string; value: number; icon?: React.ReactNode;
}) {
  return (
    <div style={{
      padding: 12, background: color + '10', borderRadius: 6,
      border: `1px solid ${color}30`,
    }}>
      <div style={{ fontSize: 11, color: '#5b6875', marginBottom: 4 }}>
        {icon} {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, color }}>
        {value}
      </div>
    </div>
  );
}
